// src/pages/InventoryMobile.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import CardComponent from "../components/CardComponent";
import { MoreVertical } from "lucide-react";

const SORT_KEYS = [
  { k: "unit_status", label: "Status" },
  { k: "unit_category", label: "Category" },
  { k: "color_code", label: "Color Code" },
  { k: "rowo", label: "RO/WO" },
  { k: "stock_po", label: "Stock PO" },
  { k: "customer", label: "Customer" },
];

// matches your DetailPage STATUS_BY_CODE (kept as-is)
const STATUS_BY_CODE = {
  0: { label: "Unassigned" },
  1: { label: "Sold" },
  2: { label: "Rented Out" },
  3: { label: "IN STK - CALGARY" },
  4: { label: "IN TRANSIT TO CALGARY" },
  5: { label: "Alert" },
  6: { label: "TO ORDER" },
  7: { label: "ON ORDER" },
  8: { label: "STK & PDI - IN CALGARY" },
  9: { label: "IN TRANSIT" },
  10: { label: "DEMO" },
  11: { label: "ALLOCATION SLOT" },
};

const CATEGORY_OPTIONS = [
  { v: "", label: "(blank)" },
  { v: "landfill", label: "Landfill" },
  { v: "sewer", label: "Sewer equipment" },
  { v: "sweeper", label: "Sweeper" },
  { v: "refuse", label: "Refuse" },
  { v: "municipal", label: "Municipal" },
  { v: "mrr", label: "MRR" },
  { v: "rail", label: "rail" },
];

function getCustomerName(r) {
  return (r.customer || "").toString().toLowerCase();
}

function norm(v) {
  return String(v ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}

function buildSearchIndex(row) {
  const parts = [];
  for (const v of Object.values(row || {})) {
    if (v == null) continue;
    if (typeof v === "object") continue;
    const s = norm(v);
    if (s) parts.push(s);
  }
  return parts.join(" | ");
}

function valStr(v) {
  if (v === null || v === undefined) return "";
  return String(v).toLowerCase();
}

function isTruthyFlag(v) {
  const s = String(v ?? "").toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function StatMiniSmall({ title, value, sub }) {
  return (
    <div
      className="scan-tile"
      style={{
        textAlign: "left",
        padding: 10,
        minHeight: 0,
      }}
    >
      <div className="scan-title" style={{ fontSize: 11, opacity: 0.8, letterSpacing: 0.2 }}>
        {title}
      </div>
      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 4, lineHeight: 1.1 }}>{value}</div>
      {sub ? (
        <div className="scan-sub" style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function UnitActionsMenu({ open, onClose, onMoveUnit, onOther1, onOther2 }) {
  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          minWidth: 0,
          borderRadius: 16,
          padding: 12,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Unit Actions</div>

        <div style={{ display: "grid", gap: 8 }}>
          <button className="btn" onClick={onMoveUnit}>
            Move Unit
          </button>
          <button className="btn-outline" onClick={onOther1}>
            Other
          </button>
          <button className="btn-outline" onClick={onOther2}>
            Other
          </button>
          <button className="btn-outline" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InventoryMobile() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(() => sessionStorage.getItem("inv_q") || "");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [cat, setCat] = useState(() => sessionStorage.getItem("inv_cat") || "__all__");
  const [sortKey, setSortKey] = useState(() => sessionStorage.getItem("inv_sort_key") || "color_code");
  const [sortDir, setSortDir] = useState(() => sessionStorage.getItem("inv_sort_dir") || "asc");

  // optional: simple local “summary” (no reports endpoint required)
  const summary = useMemo(() => {
    const total = rows.length;
    const alerts = rows.reduce((n, r) => n + (isTruthyFlag(r.alert) ? 1 : 0), 0);
    const stock = rows.reduce((n, r) => n + (isTruthyFlag(r.is_stock) ? 1 : 0), 0);
    const demo = rows.reduce((n, r) => n + (isTruthyFlag(r.is_demo) ? 1 : 0), 0);
    const rental = rows.reduce((n, r) => n + (isTruthyFlag(r.is_rental) ? 1 : 0), 0);
    return { total, alerts, stock, demo, rental };
  }, [rows]);

  // per-row actions menu
  const [actionsForId, setActionsForId] = useState(null);

  useEffect(() => sessionStorage.setItem("inv_cat", cat), [cat]);
  useEffect(() => sessionStorage.setItem("inv_q", q), [q]);
  useEffect(() => {
    sessionStorage.setItem("inv_sort_key", sortKey);
    sessionStorage.setItem("inv_sort_dir", sortDir);
  }, [sortKey, sortDir]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErrMsg("");

        const res = await api.get("/all_inventory");
        if (!alive) return;

        const data = res?.data;
        if (Array.isArray(data)) {
          setRows(data);
        } else {
          setRows([]);
          setErrMsg(`Unexpected response: ${typeof data}`);
          console.error("Inventory unexpected response:", data);
        }
      } catch (e) {
        console.error("Load failed:", e);
        if (!alive) return;
        setRows([]);
        setErrMsg(e?.response?.data?.error || "Failed to load inventory (check console/network).");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const rowsIndexed = useMemo(() => rows.map((r) => ({ ...r, __idx: buildSearchIndex(r) })), [rows]);

  const filtered = useMemo(() => {
    const terms = norm(q).split(" ").filter(Boolean);
    let arr = rowsIndexed;

    if (cat !== "__all__") arr = arr.filter((r) => norm(r.unit_category) === norm(cat));

    if (terms.length) {
      arr = arr.filter((r) => {
        const hay = r.__idx || "";
        for (const t of terms) if (!hay.includes(t)) return false;
        return true;
      });
    }

    const dir = sortDir === "desc" ? -1 : 1;

    return [...arr].sort((a, b) => {
      let va = "";
      let vb = "";

      if (sortKey === "customer") {
        va = getCustomerName(a);
        vb = getCustomerName(b);
      } else if (sortKey === "color_code") {
        va = Number(a.color_code ?? 0);
        vb = Number(b.color_code ?? 0);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      } else {
        va = valStr(a[sortKey]);
        vb = valStr(b[sortKey]);
      }

      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rowsIndexed, q, cat, sortKey, sortDir]);

  return (
    <div className="container">
      {/* Smaller stat tiles (mobile) */}
      <div
        className="scan-grid"
        style={{
          marginTop: 0,
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        <StatMiniSmall title="Total" value={String(summary.total)} sub="records" />
        <StatMiniSmall title="Alerts" value={String(summary.alerts)} sub="flagged" />
        <StatMiniSmall title="Stock / Demo / Rental" value={`${summary.stock} / ${summary.demo} / ${summary.rental}`} sub="flags" />
        <StatMiniSmall title="Showing" value={String(filtered.length)} sub="filtered" />
      </div>

      {/* Search + filters */}
      <div className="row" style={{ flexWrap: "wrap", alignItems: "center", gap: 10, marginTop: 12 }}>
        <input className="input" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />

        <select className="input" style={{ width: 220 }} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="__all__">All categories</option>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>

        <select className="input" style={{ width: 200 }} value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          {SORT_KEYS.map((s) => (
            <option key={s.k} value={s.k}>
              {s.label}
            </option>
          ))}
        </select>

        <select className="input" style={{ width: 140 }} value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
          <option value="asc">Asc</option>
          <option value="desc">Desc</option>
        </select>
      </div>

      {loading && <div className="muted" style={{ marginTop: 12 }}>Loading…</div>}
      {!loading && errMsg && <div className="muted" style={{ marginTop: 12 }}>{errMsg}</div>}
      {!loading && !errMsg && filtered.length === 0 && <div className="muted" style={{ marginTop: 12 }}>No records.</div>}

      <div className="col" style={{ marginTop: 12 }}>
        {filtered.map((r) => {
          const unitId = r.id;

          return (
            <div key={unitId} className="card" style={{ minWidth: 0, position: "relative" }}>
              {/* top row */}
              <div className="row" style={{ alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <span style={{ fontWeight: 800 }}>
                  #{unitId}
                  {r.rowo ? ` • RO/WO ${r.rowo}` : ""}
                  {r.stock_po ? ` • Stock ${r.stock_po}` : ""}
                  {r.customer ? ` • ${r.customer}` : ""}
                </span>

                <button
                  className="btn-outline"
                  style={{ padding: "6px 10px" }}
                  onClick={() => setActionsForId(unitId)}
                  aria-label="Unit actions"
                >
                  <MoreVertical size={16} />
                </button>
              </div>

              <div className="muted" style={{ marginTop: 8 }}>
                cc:{String(r.color_code ?? "").trim() || "-"} • stock:{isTruthyFlag(r.is_stock) ? "1" : "0"} • demo:
                {isTruthyFlag(r.is_demo) ? "1" : "0"} • rental:{isTruthyFlag(r.is_rental) ? "1" : "0"}
              </div>

              <div style={{ marginTop: 10 }}>
                <CardComponent
  record={r}
  table="all_inventory"
  notesTotal={0}
  notesUnread={0}
  onOpenNotes={() => alert(`Open notes for inventory #${r.id}`)}
  onAddNote={() => alert(`Add note for inventory #${r.id}`)}
/>
              </div>

              <UnitActionsMenu
                open={actionsForId === unitId}
                onClose={() => setActionsForId(null)}
                onMoveUnit={() => {
                  setActionsForId(null);
                  alert(`Move Unit: #${unitId}`);
                }}
                onOther1={() => {
                  setActionsForId(null);
                  alert(`Other action 1: #${unitId}`);
                }}
                onOther2={() => {
                  setActionsForId(null);
                  alert(`Other action 2: #${unitId}`);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
